import React from 'react'

import { CopyButton } from '@components/common/CopyButton'
import { Button, Input, useCopyToClipboard, useMediaQueryContext } from '@fourthwall/components'
import { TickIcon } from '@fourthwall/icons'

import type { GiftCard } from '../../GiftCard.types'
import { isGiftCardGroup } from '../../utils/isGiftCardGroup'
import { isGiftCardSingle } from '../../utils/isGiftCardSingle'
import * as S from './GiftCardShareBanner.styled'

type GiftCardShareBannerProps = {
  giftCard: GiftCard
}

export const GiftCardShareBanner = ({ giftCard }: GiftCardShareBannerProps) => {
  const media = useMediaQueryContext()

  const { copyToClipboard } = useCopyToClipboard({
    toastMessage: 'Gift card codes copied to clipboard',
  })

  const getTitle = () => {
    if (isGiftCardSingle(giftCard)) {
      return 'Gift card created. What’s next?'
    }

    return 'Gift cards created. What’s next?'
  }

  const getDescription = () => {
    if (isGiftCardSingle(giftCard)) {
      return 'The code can now be used during checkout. Copy or send the code to share it.'
    }

    return 'You can now share your gift cards with supporters!'
  }

  const handleCopyCodes = () => {
    if (!isGiftCardGroup(giftCard)) return

    const codes = giftCard.giftCards.map((giftCard) => giftCard.code).join('\n')

    if (codes) {
      copyToClipboard(codes)
    }
  }

  const renderGiftCardSingleFooter = () => {
    if (!isGiftCardSingle(giftCard)) return null

    return (
      <Input
        readOnly
        value={giftCard.code}
        size="small"
        actionIcon={<CopyButton copyText={giftCard.code} iconSize={16} />}
      />
    )
  }

  const renderGiftCardGroupFooter = () => {
    return (
      <Button
        appearance="secondary"
        size="small"
        label="Copy all codes to clipboard"
        onClick={handleCopyCodes}
      />
    )
  }

  const renderTick = () => {
    return (
      <S.TickContainer>
        <TickIcon height={10} />
      </S.TickContainer>
    )
  }

  return (
    <S.Container>
      {media.tablet && renderTick()}
      <S.Content>
        <S.Title>
          {!media.tablet && renderTick()}
          {getTitle()}
        </S.Title>
        <S.Description>{getDescription()}</S.Description>
        <S.Footer>
          {isGiftCardSingle(giftCard) && renderGiftCardSingleFooter()}
          {isGiftCardGroup(giftCard) && renderGiftCardGroupFooter()}
        </S.Footer>
      </S.Content>
    </S.Container>
  )
}
